// bm-design-system: pagination primitive
import { Link } from "@inertiajs/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface PaginationProps {
  page: number;
  pages: number;
  total: number;
  perPage: number;
  /** Builds the URL for a page number, keeping any filters in the query. */
  hrefFor: (page: number) => string;
  className?: string;
}

const linkClass = cn(buttonVariants({ variant: "secondary" }), "no-underline");
const disabledClass = cn(buttonVariants({ variant: "secondary" }), "pointer-events-none opacity-50");

/** "Showing 16–30 of 42" with Previous / Next. Renders nothing for an empty list. */
export function Pagination({ page, pages, total, perPage, hrefFor, className }: PaginationProps) {
  if (total === 0) return null;

  const first = (page - 1) * perPage + 1;
  const last = Math.min(page * perPage, total);

  return (
    <nav
      aria-label="Pagination"
      className={cn("flex flex-wrap items-center justify-between gap-3", className)}
    >
      <p className="text-sm text-ink-muted" aria-live="polite">
        Showing {first}–{last} of {total}
      </p>
      <div className="flex items-center gap-2">
        {page > 1 ? (
          <Link href={hrefFor(page - 1)} className={linkClass} preserveScroll>
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Previous
          </Link>
        ) : (
          <span className={disabledClass} aria-disabled="true">
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Previous
          </span>
        )}
        <span className="px-1 text-sm text-ink-muted">
          Page {page} of {pages}
        </span>
        {page < pages ? (
          <Link href={hrefFor(page + 1)} className={linkClass} preserveScroll>
            Next <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        ) : (
          <span className={disabledClass} aria-disabled="true">
            Next <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </span>
        )}
      </div>
    </nav>
  );
}
